import { Box } from "@mui/material";
import React from "react";
import { Bar, BarChart, ErrorBar, XAxis, YAxis, Tooltip } from "recharts";
import styled from "styled-components";
import { createPortal } from 'react-dom';

interface DataPoint {
  name: string;
  value: number;
  error: [number, number];
  tooltipContent?: string;
}

interface LegalOutcomesChartProps {
  data: DataPoint[];
  XAxisLabel?: string;
  YAxisLabel?: string;
  showXAxis?: boolean;
  showYAxis?: boolean;
  width?: number;
  height?: number;
  barColor?: string;
  errorBarColor?: string;
}

const defaultProps = {
  width: 600,
  height: 400,
  barColor: "#e8f5e9",
  errorBarColor: "#2e7d32",
};


const StyledTooltipBox = styled(Box)(({ theme }) => ({
  color: "#000",
  fontFamily: "Poppins",
  fontStyle: "normal",
  fontSize: "0.75rem",
  lineHeight: "166%",
  letterSpacing: "0.4px",
  padding: "8px",
  backgroundColor: "#F5F5F5",
  maxWidth: 400,
  border: "1px solid #ccc",
  borderRadius: "5px",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  position: "relative",  // Add this
  zIndex: 10000,         // Add this
}));



const CustomTooltip = ({
  active,
  payload,
  label,
  coordinate, // Recharts provides mouse position
}: {
  active?: boolean;
  payload?: any;
  label?: string;
  coordinate?: { x: number; y: number };
}) => {
  // Create a ref to track the tooltip position
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (coordinate) {
      // Get the chart's position on the page
      const chartElement = document.querySelector('.custom-bar');
      if (chartElement) {
        const rect = chartElement.getBoundingClientRect();
        setPosition({
          x: rect.left + coordinate.x,
          y: rect.top + coordinate.y - 50, // Offset to show above cursor
        });
      }
    }
  }, [coordinate]);

  if (!active || !payload?.length || !payload[0]?.payload?.tooltipContent) {
    return null;
  }

  const content = payload[0].payload.tooltipContent;
  if (!content || content.trim() === "") {
    return null;
  }

  // Render the tooltip in a portal (outside the SVG)
  return createPortal(
    <StyledTooltipBox
      style={{
        position: 'fixed',
        left: position.x - 400, 
        top: position.y,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {content}
    </StyledTooltipBox>,
    document.body
  );
};

const CustomLabel = (props: any) => {
  const { x, y, value } = props;
  const constantOffset = -30;

  return (
    <g transform={`translate(${x},${y})`}>
      <circle
        cx={55}
        cy={constantOffset}
        r={20}
        fill="#00796B"
        className="shadow-lg"
      />
      <text
        x={55}
        y={constantOffset}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-sm font-semibold fill-current text-gray-700"
      >
        {`${value}%`}
      </text>
    </g>
  );
};

const CustomYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const isLastTick = payload.value === 0;
  const dx = isLastTick ? -58 : -70;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={dx}
        y={0}
        dy={15}
        fill="#666666"
        fontSize={16}
        textAnchor="start"
      >{`${payload.value}`}</text>
    </g>
  );
};

const BarChartWithErrors: React.FC<LegalOutcomesChartProps> = ({
  data,
  XAxisLabel,
  YAxisLabel,
  showXAxis = true,
  showYAxis = true,
  width = defaultProps.width,
  height = defaultProps.height,
  barColor = defaultProps.barColor,
  errorBarColor = defaultProps.errorBarColor,
}) => {
  return (
    <Box
      sx={{
        "& .recharts-tooltip-cursor,& .recharts-tooltip-cursor:hover": {
          fill: "none !important",
        },
      }}
      width={"100%"}
    >
      <BarChart
        width={width}
        height={height + 5}
        data={data}
        className="custom-bar"
        margin={{
          top: 0,
          right: 50,
          left: 18,
          bottom: -5,
        }}
      >
        {showXAxis && (
          <XAxis
            axisLine={false}
            dataKey="name"
            tick={{ fill: "#666666", fontSize: "0.75rem" }}
            label={{
              fontSize: "0.75rem",
              value: XAxisLabel,
            }}
          />
        )}
        {showYAxis && (
          <YAxis
            interval={0}
            axisLine={false}
            tickLine={{ stroke: "#666666", strokeWidth: 0 }}
            // tick={{ fill: "#666666", fontSize: 16, textAnchor: "start" }}
            tick={<CustomYAxisTick />}
            strokeWidth={1}
            dy={10}
            dx={-60}
            label={{
              fontSize: "0.75rem",
              value: YAxisLabel,
              angle: -90,
              position: "left",
              dy: -50,
              style: { fill: "#666666" },
              offset: 10,
            }}
            // Extra headroom above 100 so tall bars, their error whiskers and
            // the floating percentage labels are not clipped at the plot top
            domain={[0, 140]}
            ticks={[0, 100]}
          />
        )}
        <svg>
          <path
            d={`
              M20,20   
              L20,${height - 30}
              A20,20 0 0 0 40,${height - 20} 
              L${width},${height - 20}
            `}
            stroke="#666666"
            opacity={0.2}
            strokeWidth="1"
            fill="none"
          />
        </svg>
        <Tooltip
          payload={data}
          content={<CustomTooltip />}
          allowEscapeViewBox={{ x: true, y: true }}  // Add this
          position={{ x: undefined, y: undefined }}   // Let Recharts auto-position
        />
        <Bar
          label={<CustomLabel />}
          dataKey="value"
          barSize={60}
          fill={barColor}
          radius={[50, 50, 0, 0]}
        >
          <ErrorBar
            dataKey="error"
            stroke={errorBarColor}
            strokeDasharray={"3 0"}
            strokeWidth={2}
          />
        </Bar>
      </BarChart>
    </Box>
  );
};

export default BarChartWithErrors;
